/**
 * Lógica pura de empréstimo de itens do acervo.
 * O empréstimo vive no próprio item (`item.loan`), evitando nova coleção/regra.
 * Formato: { borrowerName, quantity, dueDate?, loanedAt }.
 */

/** Item está emprestado? */
export function isLoaned(item) {
  return !!(item && item.loan && item.loan.borrowerName);
}

/** Sanitiza a quantidade emprestada: inteiro entre 1 e o total do item. */
export function clampLoanQuantity(qty, item) {
  const total = typeof item?.quantity === 'number' && item.quantity > 0 ? item.quantity : Infinity;
  let n = parseInt(String(qty), 10);
  if (Number.isNaN(n) || n < 1) n = 1;
  if (n > total) n = total === Infinity ? n : total;
  return n;
}

function parseDueMoment(dueDate) {
  if (!dueDate) return null;
  const s = String(dueDate);
  const withTime = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (withTime) {
    const [, y, m, d, hh, mm] = withTime;
    return new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), 0, 0);
  }
  const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    return new Date(Number(y), Number(m) - 1, Number(d), 23, 59, 59, 999);
  }
  return null;
}

/** Empréstimo vencido? Compara a data de devolução com `now`. */
export function isOverdue(loan, now = new Date()) {
  const due = parseDueMoment(loan && loan.dueDate);
  if (!due) return false;
  return now.getTime() > due.getTime();
}

/** Dias restantes até a devolução (negativo se vencido). null se sem data. */
export function daysUntilDue(loan, now = new Date()) {
  const due = parseDueMoment(loan && loan.dueDate);
  if (!due) return null;
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((dueDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

/** Monta o objeto de empréstimo a persistir no item. */
export function buildLoan({ borrowerName, borrowerGuestId, borrowerGroup, borrowerPhone, borrowerEmail, quantity, dueDate, item, now = new Date() } = {}) {
  const name = String(borrowerName ?? '').trim();
  if (!name) return { ok: false, reason: 'missing_borrower' };
  const loan = {
    loanId: `ln_${now.getTime().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
    borrowerName: name,
    quantity: clampLoanQuantity(quantity, item),
    loanedAt: now.toISOString(),
  };
  if (dueDate && /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/.test(String(dueDate))) loan.dueDate = String(dueDate);
  if (borrowerGuestId) loan.borrowerGuestId = String(borrowerGuestId);
  if (borrowerGroup) loan.borrowerGroup = String(borrowerGroup);
  if (borrowerPhone) loan.borrowerPhone = String(borrowerPhone);
  if (borrowerEmail) loan.borrowerEmail = String(borrowerEmail);
  return { ok: true, loan };
}

/** Filtra apenas os itens emprestados. */
export function filterLoanedItems(items) {
  if (!Array.isArray(items)) return [];
  return items.filter(isLoaned);
}

/** Texto curto do status do empréstimo. */
export function describeLoan(loan, now = new Date()) {
  if (!loan || !loan.borrowerName) return '';
  const base = `Com ${loan.borrowerName}`;
  if (!loan.dueDate) return base;
  const days = daysUntilDue(loan, now);
  if (days == null) return base;
  if (days < 0) return `${base} · atrasado ${Math.abs(days)}d`;
  if (days === 0) return `${base} · devolve hoje`;
  return `${base} · devolve em ${days}d`;
}
